// Onboarding funnel — /signup on both hosts (see newAppPath; the old /new and
// /app/new paths redirect here). One bundle, two sites.
// Self-service, no admin in the loop, AGENT-FIRST:
//   account (email + password) -> verify (emailed code) -> connect an agent
//     [optional manual branch: slug -> provisioning -> done]
// The primary handoff after verification is the agent code — an AI agent then
// creates and deploys apps headlessly. Creating a project in the browser stays
// available as the secondary path. A signed-in returning user skips straight
// to the slug step (the agent hand-off has its own page, /agent, linked from the
// app list's New-app tile and the account menu).
//
// Account creation uses Cognito's public sign-up API with email verification
// (a human reads the code — secure, no platform changes). Picking a slug then
// enqueues `provision-project` DIRECTLY (no approval queue); provisioning seeds
// the signed-in user as the project's operator-owner (platform-pool
// `{slug}:operator-owner`), so console access and `s3p deploy` ride their
// platform identity — no per-project agent code or password.

import {
	Alert,
	Anchor,
	Button,
	Card,
	Code,
	Collapse,
	CopyButton,
	Divider,
	Group,
	Loader,
	PasswordInput,
	Stack,
	Text,
	TextInput,
	Title,
} from "@mantine/core";
import type { PlatformClient } from "@s3p/sdk";
import {
	IconCheck,
	IconCircleCheck,
	IconCopy,
	IconExclamationCircle,
	IconExternalLink,
	IconServer,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { connect } from "/sdk/index.js";
import {
	type ProjectManifest,
	type ProvisionResult,
	SLUG_RE,
} from "../../lib/contracts.ts";
import { ConnectAgent } from "../../components/ConnectAgent.tsx";
import { Wordmark } from "../../components/Footer.tsx";
import { StartWithAgent } from "../../components/StartWithAgent.tsx";
import { CONSOLE_ORIGIN, MARKETING_ORIGIN, newAppPath } from "../../lib/hosts.ts";
import { useAuthFlow } from "../../session/useAuthFlow.ts";
import { useSession } from "../../session/SessionProvider.tsx";

type Phase = "account" | "verify" | "connect" | "slug" | "provisioning" | "done";
type Availability =
	| "idle"
	| "invalid"
	| "checking"
	| "available"
	| "taken"
	| "error";

const PROVISION_TIMEOUT_MS = 6 * 60 * 1000;
const PROVISION_POLL_MS = 3000;

/** Cognito provider NAMES aren't all button-friendly (e.g. SignInWithApple). */
function providerLabel(name: string): string {
	if (name === "SignInWithApple") return "Apple";
	if (name === "OIDC") return "your organization";
	return name;
}


function nameFromSlug(slug: string): string {
	return slug
		.split("-")
		.filter(Boolean)
		.map((w) => w[0]?.toUpperCase() + w.slice(1))
		.join(" ");
}

export function CreateApp() {
	const session = useSession();
	const [phase, setPhase] = useState<Phase>("account");
	const [authedClient, setAuthedClient] = useState<PlatformClient | null>(null);

	// ACCOUNT + VERIFY are driven by the SDK's `app.auth.flow()` machine — the
	// same one the sign-in page and the vanilla test app use. It reads THIS
	// project's published methods (password? providers? signup open?) and walks
	// identify → password → verify → done, so the funnel's first screen renders
	// whatever's configured with no branching here.
	const { flow, state: flowState } = useAuthFlow();
	// Sign-up intent (providers become "Sign up with…", passwordless mints a
	// sealed password) the moment the machine is ready.
	useEffect(() => {
		flow?.setIntent("signup");
	}, [flow]);
	const methods = flowState?.methods;
	const authStep = flowState?.step ?? "loading";
	const authBusy = flowState?.busy ?? false;
	const authError = flowState?.error;
	const accountExists = /exist/i.test(authError ?? "");

	// Local field values (the machine owns the flow, the inputs stay with the UI).
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [code, setCode] = useState("");
	// The agent hand-off is a secondary path now — the human form leads (mirrors
	// the sign-in card). Expanded on demand so it no longer dwarfs the form.
	const [showAgent, setShowAgent] = useState(false);

	// Combined single-card sign-up: identify (email) then create (password) in
	// one click. `identify` advances the machine to "password" synchronously for
	// the signup intent; guard in case the email was rejected.
	async function submitAccount() {
		if (!flow) return;
		await flow.identify(email.trim());
		if (flow.get().step !== "password") return; // invalid email → error shown
		await flow.usePassword(methods?.password ? password : "");
	}

	// When the machine finishes (account created, verified, signed in), pick up
	// the adopted session and hand the funnel to the provisioning steps.
	useEffect(() => {
		if (flowState?.step !== "done") return;
		void (async () => {
			const app = await connect();
			setAuthedClient(app);
			setPhase("connect");
		})();
	}, [flowState?.step]);

	// slug step
	const [slug, setSlug] = useState("");
	const [availability, setAvailability] = useState<Availability>("idle");
	const [checkError, setCheckError] = useState<string | null>(null);

	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const client = authedClient ?? session.client;
	const me = authedClient?.me ?? session.me;
	const trimmed = slug.trim();
	const returning = session.signedIn && !authedClient;
	// The owner is the signed-in platform user; their email is already verified
	// in the platform pool, so we trust it and seed it `email_verified=true` in
	// the new project's pool. `me.username` is the email for the platform pool.
	const ownerEmail = me?.email ?? me?.username ?? "";
	const projectHost = trimmed ? `${trimmed}.s3p.dev` : "";

	// A returning, already-signed-in user skips the account/verify steps.
	// biome-ignore lint/correctness/useExhaustiveDependencies: jump to slug once when the existing session is ready.
	useEffect(() => {
		if (session.ready && session.signedIn && phase === "account")
			setPhase("slug");
	}, [session.ready, session.signedIn]);

	// Debounced slug uniqueness check against the projects collection.
	useEffect(() => {
		if (phase !== "slug") return;
		if (!trimmed) return setAvailability("idle");
		if (!SLUG_RE.test(trimmed)) return setAvailability("invalid");
		if (!client) return;
		setAvailability("checking");
		setCheckError(null);
		let cancelled = false;
		const timer = setTimeout(async () => {
			try {
				const projects = client.collection<ProjectManifest>("projects", {
					filename: "manifest.json",
				});
				const existing = await projects.get(trimmed);
				if (!cancelled) setAvailability(existing ? "taken" : "available");
			} catch (e) {
				if (!cancelled) {
					setAvailability("error");
					setCheckError(e instanceof Error ? e.message : String(e));
				}
			}
		}, 400);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [trimmed, client, phase]);


	async function submitSlug() {
		if (!client || !me || availability !== "available") return;
		if (!ownerEmail) {
			setError(
				"Couldn't determine your account email to set as the project owner. Try signing out and back in.",
			);
			return;
		}
		setBusy(true);
		setError(null);
		setPhase("provisioning");
		try {
			// No password: the creator becomes the project's operator-owner (their
			// platform identity), so the console and `s3p deploy` ride their platform
			// login — nothing per-project to choose or store.
			if (!client.projects) throw new Error("project lifecycle unavailable on this client");
			const res: ProvisionResult = await client.projects.create(
				{
					slug: trimmed,
					name: nameFromSlug(trimmed) || trimmed,
					ownerEmail,
				},
				{ timeoutMs: PROVISION_TIMEOUT_MS, pollIntervalMs: PROVISION_POLL_MS },
			);
			if (!res?.slug) throw new Error("provisioning returned no result");
			setPhase("done");
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
			setPhase("slug");
		} finally {
			setBusy(false);
		}
	}

	// ── Render ──────────────────────────────────────────────
	if (!session.ready) {
		return (
			<Group justify="center" mt={80}>
				<Loader />
			</Group>
		);
	}

	const slugCard = (
		<Card withBorder radius="md" padding="lg" mt={returning ? 0 : "md"}>
			<Stack>
				<TextInput
					label="Project slug"
					description="Lowercase letters, digits, and hyphens. Names the bucket and Cognito resources — can't change later."
					placeholder="my-app"
					value={slug}
					onChange={(e) => setSlug(e.currentTarget.value)}
					rightSection={
						availability === "checking" ? <Loader size="xs" /> : null
					}
					error={
						availability === "invalid"
							? "Lowercase letters, digits, hyphens only"
							: availability === "taken"
								? "That slug is already taken"
								: undefined
					}
				/>
				{availability === "available" && (
					<Group gap={6} c="teal">
						<IconCircleCheck size={16} />
						<Text size="sm">
							<Code>{trimmed}</Code> is available — it'll be "
							{nameFromSlug(trimmed)}"
						</Text>
					</Group>
				)}
				{availability === "error" && (
					<Group gap={6} c="red">
						<IconExclamationCircle size={16} />
						<Text size="sm">Couldn't check availability: {checkError}</Text>
					</Group>
				)}
				<Text size="xs" c="dimmed">
					Creating an app provisions a dedicated bucket, Cognito pool, and IAM
					roles, then makes you its owner. You'll manage it from its{" "}
					project console and deploy with <Code>s3p deploy</Code> —
					both using your existing platform login, no separate password. No
					approval needed; it's ready in a minute or two.
				</Text>
				{error && (
					<Alert color="red" variant="light" title="Couldn't create app">
						{error}
					</Alert>
				)}
				<Group justify="flex-end">
					<Button component={Link} to="/" variant="default">
						Cancel
					</Button>
					<Button
						onClick={submitSlug}
						loading={busy}
						disabled={availability !== "available"}
					>
						Create app
					</Button>
				</Group>
			</Stack>
		</Card>
	);

	const provisioningCard = (
		<Card withBorder radius="md" padding="lg" mt={returning ? 0 : "md"}>
			<Stack gap="md" align="center" py="md">
				<Loader />
				<Text c="dimmed" size="sm" ta="center">
					Provisioning <Code>{trimmed}</Code> — creating its bucket, Cognito
					pool, and IAM roles, and making you the owner. This takes a minute or
					two.
				</Text>
			</Stack>
		</Card>
	);

	const doneCard = (
		<Card withBorder radius="md" padding="lg" mt={returning ? 0 : "md"}>
			<Stack gap="md">
				<Group gap="xs">
					<IconCircleCheck size={22} color="var(--mantine-color-teal-5)" />
					<Title order={3}>App created</Title>
				</Group>
				<Text c="dimmed" size="sm">
					<Code>{trimmed}</Code> is provisioned and you're its owner. Sign in to
					either with your platform login — there's no separate password.
				</Text>
				{/* These were <Code> strings the user had to RETYPE. The /server URL
				    appeared nowhere else in the UI, so the agent-path user never
				    learned it existed at all. */}
				<Group>
					<Button
						component="a"
						href={`https://${projectHost}/`}
						target="_blank"
						variant="light"
						leftSection={<IconExternalLink size={16} />}
					>
						Open app
					</Button>
					<Button
						component="a"
						href={`${CONSOLE_ORIGIN}/${trimmed}`}
						target="_blank"
						leftSection={<IconServer size={16} />}
					>
						Open console
					</Button>
				</Group>
				<Stack gap="xs">
					<Text size="sm">Deploy from the CLI using your platform login:</Text>
					<CopyButton value={`s3p init ${trimmed} && s3p deploy`}>
						{({ copied, copy }) => (
							<Button
								variant="light"
								color={copied ? "teal" : undefined}
								leftSection={
									copied ? <IconCheck size={16} /> : <IconCopy size={16} />
								}
								onClick={copy}
							>
								{copied ? "Copied" : "Copy commands"}
							</Button>
						)}
					</CopyButton>
					<Text size="xs" c="dimmed">
						<Code>s3p init {trimmed}</Code> writes the project config; deploy uses
						your platform session (run <Code>s3p login</Code> against the platform
						once). No per-project credential.
					</Text>
				</Stack>
				<Group>
					<Button onClick={() => window.location.assign("/")}>
						Go to your apps
					</Button>
				</Group>
			</Stack>
		</Card>
	);

	const connectCard = (
		<Stack gap="sm" mt="md">
			<ConnectAgent />
			<Card withBorder radius="md" padding="md">
				<Group justify="space-between" wrap="wrap">
					<Text size="sm" c="dimmed">
						Prefer to do it yourself in the browser?
					</Text>
					<Group gap="sm">
						<Button variant="default" onClick={() => window.location.assign("/")}>
							Go to your apps
						</Button>
						<Button variant="light" onClick={() => setPhase("slug")}>
							Create an app manually →
						</Button>
					</Group>
				</Group>
			</Card>
		</Stack>
	);

	const slugStepCard =
		phase === "done"
			? doneCard
			: phase === "provisioning"
				? provisioningCard
				: slugCard;

	const finalStepCard = phase === "connect" ? connectCard : slugStepCard;

	// Returning users: just the slug step (+ provisioning/done).
	if (returning) {
		return (
			<Stack gap="xl" maw={640}>
				<Stack gap={2}>
					<Title order={1}>Create a new app</Title>
					<Text c="dimmed" size="sm">
						Signed in as {me?.email || me?.username}. Pick a slug to get
						started.
					</Text>
				</Stack>
				{slugStepCard}
			</Stack>
		);
	}

	// New users: account → verify → app funnel. Header MIRRORS the sign-in page
	// (wordmark + title + subtitle); the phases render conditionally — no stepper
	// chrome, so sign-up reads exactly like sign-in.
	return (
		<Stack gap="xl" maw={440} mx="auto">
			<Stack gap={2}>
				<Anchor href={MARKETING_ORIGIN} underline="never" w="fit-content" mb="md">
					<Wordmark size={26} />
				</Anchor>
				<Title order={1}>Create your account</Title>
				<Text c="dimmed" size="sm">
					One platform account works everywhere: this console, every project you
					operate, and the CLI.
				</Text>
			</Stack>

			{phase === "account" && (
				<>
					{/* ACCOUNT + VERIFY, rendered from the flow machine (intent=signup).
					    identify/password → the single sign-up card; verify → the code
					    card. Same machine as the sign-in page and the test app. */}
					{(authStep === "identify" || authStep === "password") && (
						<>
							{/* The human form leads and MIRRORS the sign-in card: providers on
							    top, then email (+ password when enabled), then the primary
							    action, then a door for people who already have a key. */}
							<Card withBorder radius="md" padding="lg">
								<Stack>
									{/* Social first — needs no email typed. Shown when signup is open. */}
									{methods?.signupOpen &&
										methods.providers.map((name) => (
											<Button
												key={name}
												variant="default"
												fullWidth
												loading={authBusy}
												onClick={() => void flow?.useProvider(name)}
											>
												Sign up with {providerLabel(name)}
											</Button>
										))}
									{methods?.signupOpen && methods.providers.length > 0 && (
										<Divider label="or" labelPosition="center" />
									)}
									<TextInput
										label="Email"
										type="email"
										autoComplete="email"
										placeholder="you@example.com"
										value={email}
										onChange={(e) => setEmail(e.currentTarget.value)}
									/>
									{methods?.password && (
										<PasswordInput
											label="Password"
											placeholder="At least 10 characters"
											value={password}
											onChange={(e) => setPassword(e.currentTarget.value)}
										/>
									)}
									{methods && !methods.password && (
										<Text size="sm" c="dimmed">
											No password needed — we'll email you a code to finish signing in.
										</Text>
									)}
									{authError && (
										<Alert
											color={accountExists ? "yellow" : "red"}
											variant="light"
											title="Heads up"
										>
											{authError}{" "}
											{accountExists && (
												<Anchor onClick={() => session.signIn(newAppPath())}>
													Sign in instead →
												</Anchor>
											)}
										</Alert>
									)}
									<Button
										fullWidth
										onClick={submitAccount}
										loading={authBusy}
										disabled={
											!email.trim() || (methods?.password && password.length < 10)
										}
									>
										Create account
									</Button>

									{/* A door for people who already have a key — quiet footer. */}
									<Text ta="center" size="sm" c="dimmed">
										Already have an s3p account?{" "}
										<Anchor size="sm" onClick={() => session.signIn(newAppPath())}>
											Sign in
										</Anchor>
									</Text>
								</Stack>
							</Card>

							{/* SECONDARY: the agent hand-off, opt-in. */}
							<Stack gap="xs" mt="lg">
								<Divider
									label="or let a coding agent do it end-to-end"
									labelPosition="center"
								/>
								<Anchor size="sm" ta="center" onClick={() => setShowAgent((v) => !v)}>
									{showAgent ? "Hide agent hand-off" : "Start with an agent instead →"}
								</Anchor>
								<Collapse in={showAgent}>
									<StartWithAgent />
								</Collapse>
							</Stack>
						</>
					)}

					{authStep === "verify" && (
						<Card withBorder radius="md" padding="lg">
							<Stack>
								<Text size="sm" c="dimmed">
									We emailed a verification code to <Code>{email.trim()}</Code>.
									Enter it below.
								</Text>
								<TextInput
									label="Verification code"
									placeholder="123456"
									value={code}
									onChange={(e) => setCode(e.currentTarget.value)}
								/>
								{authError && (
									<Alert color="red" variant="light" title="Couldn't verify">
										{authError}
									</Alert>
								)}
								<Group justify="space-between">
									<Anchor size="sm" onClick={() => void flow?.resend()}>
										Resend code
									</Anchor>
									<Button
										onClick={() => void flow?.confirmSignup(code)}
										loading={authBusy}
										disabled={!code.trim()}
									>
										Verify &amp; continue
									</Button>
								</Group>
							</Stack>
						</Card>
					)}

					{(authStep === "loading" || authStep === "done") && (
						<Group justify="center" py="xl">
							<Loader />
						</Group>
					)}
				</>
			)}

			{phase !== "account" && finalStepCard}
		</Stack>
	);
}
